import { useQuery } from '@tanstack/react-query';
import type { AdminWorkspace } from '@app/shared';
import { api } from './api';
import { qk } from './queryKeys';
import { useWorkspaceId } from './auth';

/**
 * Operator cross-tenant workspace list (§14.6, #278), fetched TRY-LOAD-THEN-HIDE: the
 * `me` payload carries no operator flag, so we attempt the operator-only endpoint and let
 * the result decide. A non-operator caller gets 403 → the query errors → the operator UI
 * (the workspace dropdown) renders nothing. `retry:false` fails a 403 fast; a modest
 * `staleTime` shares ONE fetch across the Agents + Runs pages and the run detail within a
 * session. `query.isSuccess` ⇒ the caller is a platform operator. Mirrors the operator gate
 * on the Platform dashboard (`PlatformDashboardPage`) + its Admin entry-point card.
 */
export function useOperatorWorkspaces() {
  return useQuery({
    queryKey: qk.adminWorkspaces(),
    queryFn: () => api.listAdminWorkspaces(),
    retry: false,
    staleTime: 60_000,
  });
}

export interface ScopedWorkspace {
  /** The workspace id the page's list/detail queries + SSE should use. */
  workspaceId: string;
  /** The caller's own (default) workspace — what a non-operator always uses. */
  ownWorkspaceId: string;
  /**
   * True when viewing a workspace that is NOT the caller's own — write affordances are
   * hidden (the server keeps writes member/owner-gated, so they would 404 anyway).
   */
  viewOnly: boolean;
  /** Whether the caller is a platform operator (the admin workspace list loaded). */
  isOperator: boolean;
  /** Every workspace (operator only); empty for a non-operator. */
  workspaces: AdminWorkspace[];
  /** The selected workspace's row (for the owner label), when known. */
  selected: AdminWorkspace | null;
}

/**
 * Resolve the workspace a page should be scoped to, given the optional `ws` URL search
 * param the operator dropdown writes. A NON-operator (the admin list 403s) never leaves
 * their own workspace — the param is IGNORED, so a non-operator's page is behaviourally
 * identical to before. Selecting the own workspace behaves exactly as before
 * (`viewOnly=false`); any OTHER workspace is view-only (an operator's reads are allowed by
 * the server, writes stay member-gated).
 */
export function useScopedWorkspace(wsParam: string | undefined): ScopedWorkspace {
  const ownWorkspaceId = useWorkspaceId();
  const ops = useOperatorWorkspaces();
  const isOperator = ops.isSuccess;
  const workspaces = ops.data ?? [];
  const workspaceId = isOperator && wsParam ? wsParam : ownWorkspaceId;
  const viewOnly = workspaceId !== ownWorkspaceId;
  const selected = workspaces.find((w) => w.id === workspaceId) ?? null;
  return { workspaceId, ownWorkspaceId, viewOnly, isOperator, workspaces, selected };
}
